import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin, Loader2 } from "lucide-react";
import Map from "@/components/Map";
import ShopCard from "@/components/ShopCard";
import api from "@/lib/api";
import { normalizeShop, normalizeCategories, fetchShopsNearby } from "@/lib/adapt";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

const DEFAULT_CENTER = { lat: 10.776, lng: 76.654 };

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [locationName, setLocationName] = useState("Palakkad");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [radius, setRadius] = useState(50);
  const [q, setQ] = useState("");
  const [shops, setShops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [geoResults, setGeoResults] = useState([]);
  const [locQuery, setLocQuery] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    api
      .get("/categories")
      .then((r) => setCategories(normalizeCategories(r.data?.categories)))
      .catch(() => {});
  }, []);

  const fetchShops = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const list = await fetchShopsNearby(api, {
          lat: center.lat,
          lng: center.lng,
          radius,
          category: category === "all" ? undefined : category,
          q: q || undefined,
        });
        setShops(list);
      } catch {
        setShops([]);
      } finally {
        setLoading(false);
      }
    },
    [center.lat, center.lng, radius, category, q]
  );

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  useEffect(() => {
    if (category === "all") params.delete("category");
    else params.set("category", category);
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Geocode search (Nominatim proxy)
  const onLocInput = (value) => {
    setLocQuery(value);
    clearTimeout(debounceRef.current);
    if (value.length < 3) {
      setGeoResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode/search", { params: { q: value } });
        setGeoResults(data?.results || []);
      } catch {
        setGeoResults([]);
      }
    }, 400);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationName("Your location");
      },
      () => {}
    );
  };

  return (
    <div className="min-h-screen bg-secondary/50 pt-[72px]">
      {/* Filter bar */}
      <div className="sticky top-[72px] z-30 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 py-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              data-testid="browse-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search shops, e.g. coffee, bakery…"
              className="w-full rounded-full bg-secondary border border-border pl-11 pr-4 py-3 text-sm font-medium focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Location search */}
          <div className="relative min-w-[200px]">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
            <input
              data-testid="browse-location"
              value={locQuery}
              onChange={(e) => onLocInput(e.target.value)}
              placeholder={locationName}
              className="w-full rounded-full bg-secondary border border-border pl-11 pr-4 py-3 text-sm font-medium focus:outline-none focus:border-primary transition-colors"
            />
            {geoResults.length > 0 && (
              <div className="absolute z-40 mt-2 w-[320px] max-w-[90vw] bg-card rounded-2xl border border-border shadow-xl overflow-hidden">
                {geoResults.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`geo-result-${i}`}
                    onClick={() => {
                      setCenter({ lat: g.lat, lng: g.lng });
                      setLocationName(g.display_name.split(",")[0]);
                      setLocQuery("");
                      setGeoResults([]);
                    }}
                    className="block w-full text-left px-4 py-3 text-sm hover:bg-secondary transition-colors border-b border-border last:border-0"
                  >
                    {g.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="browse-category" className="w-[170px] rounded-full bg-secondary border-border py-6">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-3 min-w-[220px] px-4 py-2 rounded-full bg-secondary border border-border">
            <SlidersHorizontal className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex-1">
              <Slider
                data-testid="browse-radius"
                value={[radius]}
                min={1}
                max={50}
                step={1}
                onValueChange={(v) => setRadius(v[0])}
              />
            </div>
            <span className="text-sm font-bold w-12 text-right">{radius} km</span>
          </div>

          <button
            data-testid="browse-my-location"
            onClick={useMyLocation}
            className="rounded-full bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Use my location
          </button>
        </div>
      </div>

      {/* Split view */}
      <div className="mx-auto max-w-[1600px] grid lg:grid-cols-[1fr_1.1fr] h-[calc(100vh-72px-81px)]">
        {/* List */}
        <div className="overflow-y-auto p-4 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h1 className="font-display font-bold text-2xl tracking-tight">
              {loading ? "Finding shops…" : `${shops.length} shops nearby`}
            </h1>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-72 rounded-3xl bg-secondary animate-pulse" />
              ))}
            </div>
          ) : shops.length === 0 ? (
            <div className="grid place-items-center h-64 text-center">
              <div>
                <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="font-display font-bold text-xl">No shops in this range</p>
                <p className="text-muted-foreground mt-1">Try widening the radius or changing filters.</p>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="browse-shop-list">
              {shops.map((s, i) => (
                <ShopCard key={s.id} shop={s} index={i} onHover={setActiveId} />
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div className="relative hidden lg:block h-full sticky top-0">
          {loading && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[400] flex items-center gap-2 bg-card px-4 py-2 rounded-full border border-border shadow">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm font-semibold">Updating map</span>
            </div>
          )}
          <Map center={center} shops={shops} activeId={activeId} onSelect={setActiveId} />
        </div>
      </div>
    </div>
  );
}
